import Request from "@/ts/Request";
import tinycolor from "tinycolor2";
import { VueMaskDirective } from "v-mask";
import Vue from "vue";
import VueAnalytics from "vue-analytics";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import App from "./App.vue";
import { getApplicationUrl } from "./config";
import "./directives/contrast.ts";
import "./directives/lazyLoad.ts";
import { initializeFirebase } from "./firebaseInit";
import "./font-awesome-imports";
import vuetify from "./plugins/vuetify";
import router from "./router";
import GoogleTagManagerService from "./services/google-tag-manager.service";
import store from "./store";
import "./stylesheet/app.scss";
import "./stylesheet/components.scss";
import "./stylesheet/homes.scss";
import "./stylesheet/views.scss";
import "./thirdparty/card/card.css";
import "./thirdparty/card/card.js";
import "./thirdparty/fonts.css";
import "./thirdparty/slick/slick-theme.css";
import "./thirdparty/slick/slick.css";
import "./thirdparty/slick/slick.js";
import "./thirdparty/to-title-case.js";
import "./thirdparty/vivify.min.css";
import { registerServiceWorker } from "./ts/registerServiceWorker";

if (window.location.origin === "https://salgadoscarvalho.eattake.com") {
  const newUrl = window.location.href.replace(
    "https://salgadoscarvalho.eattake.com",
    "https://sal.eattake.com"
  );
  window.location.href = newUrl;
}

Vue.directive("mask", VueMaskDirective);

Vue.config.productionTip = true;
Vue.config.devtools = true;
Vue.use(Toast, {
  transition: "Vue-Toastification__bounce",
  maxToasts: 5,
  newestOnTop: true,
});

let keyAnalytics = "";
let gtmAuth = "";
let gtmPreview = "";
declare let CustomConfigParameters: any;
const testNRORG = "";

async function safeStartApp() {
  try {
    await registerServiceWorker();
  } catch (e) {
    console.warn(
      "Falha ao registrar o Service Worker, iniciando app mesmo assim."
    );
  }

  startApp();
}

if (window.cordova) {
  const paramkeyArray = ["nrorg"];
  document.addEventListener("deviceready", () => {
    if (typeof CustomConfigParameters === "undefined") {
      safeStartApp();
    } else
      CustomConfigParameters.get(
        (configData: any) => {
          sessionStorage.setItem("INIT_NRORG", configData.nrorg);
          safeStartApp();
        },
        (err: any) => {
          safeStartApp();
        },
        paramkeyArray
      );
  });
  document.addEventListener("resume", () => {
    if (!sessionStorage.getItem("INIT_NRORG")) {
      if (typeof CustomConfigParameters === "undefined") {
        safeStartApp();
      } else
        CustomConfigParameters.get(
          (configData: any) => {
            sessionStorage.setItem("INIT_NRORG", configData.nrorg);
            safeStartApp();
          },
          (err: any) => {
            safeStartApp();
          },
          paramkeyArray
        );
    }
  });
} else {
  getOrganizationDataByURL();
}

function getOrganizationDataByURL() {
  try {
    const url = getApplicationUrl();
    const req = new Request();
    req.blockTouchEvents = false;
    req.body = {
      URL: url,
    };
    req.route = "getOrganizationDataByURL";
    req.callbackSuccess = function (response: any) {
      const name: string = response.organization.NAME;
      const editableFields: any = response.organization.EDITABLE_FIELDS;
      const PRIMARY_COLOR = response.organization.CONFIGURATIONS.PRIMARY_COLOR;
      const SUPPORT_COLOR =
        response.organization.CONFIGURATIONS.SECONDARY_COLOR;
      const PRIMARY_COLOR_LIGHT = tinycolor(PRIMARY_COLOR)
        .lighten(35)
        .toHexString();

      keyAnalytics = response.organization.CONFIGURATIONS.GOOGLE_ANALYTICS_KEY;

      sessionStorage.setItem("NRORG", response.organization.NRORG);
      sessionStorage.setItem("INIT_NRORG", response.organization.NRORG);
      sessionStorage.setItem("ORG_NAME", name);
      sessionStorage.setItem("ORG_FIELDS", JSON.stringify(editableFields));
      sessionStorage.setItem(
        "ORG_PREFERENCES",
        JSON.stringify(response.organization.CONFIGURATIONS.PREFERENCE_FLOW)
      );
      sessionStorage.setItem(
        "ORGANIZATION_DATA",
        JSON.stringify(response.organization)
      );
      sessionStorage.setItem(
        "ORGANIZATION_LOGO",
        response.organization.CONFIGURATIONS.LOGO_IMAGE
      );
      sessionStorage.setItem(
        "ORGANIZATION_LOGO_SMALL",
        response.organization.CONFIGURATIONS.LOGO_IMAGE_SMALL
      );
      sessionStorage.setItem(
        "HOME_LAYOUT",
        response.organization.CONFIGURATIONS.APP_LAYOUT_CONFIG.homeLayout
      );

      setIcon();

      const rootStyle = document.documentElement.style;

      if (rootStyle) {
        rootStyle.setProperty("--primary-color", PRIMARY_COLOR);
        rootStyle.setProperty("--secundary-color", SUPPORT_COLOR);
        rootStyle.setProperty("--primary-color-light", PRIMARY_COLOR_LIGHT);
        rootStyle.setProperty("--support-color", SUPPORT_COLOR);
        rootStyle.setProperty("--secundary-support-color", SUPPORT_COLOR);
      }

      setThemeColor(PRIMARY_COLOR);
      safeStartApp();
    };
    req.callbackError = function (error: any) {
      console.log(error);
      if (testNRORG) {
        sessionStorage.setItem("INIT_NRORG", testNRORG);
        safeStartApp();
      } else if (sessionStorage.getItem("NRORG")) {
        const nrorg = sessionStorage.getItem("NRORG") || "0";
        sessionStorage.setItem("INIT_NRORG", nrorg);
        safeStartApp();
      } else {
        safeStartApp();
      }
    };
    req.showLoader = false;
    req.send();
  } catch (e) {
    if (testNRORG) {
      sessionStorage.setItem("INIT_NRORG", testNRORG);
      safeStartApp();
    } else if (sessionStorage.getItem("NRORG")) {
      const nrorg = sessionStorage.getItem("NRORG") || "0";
      sessionStorage.setItem("INIT_NRORG", nrorg);
      safeStartApp();
    } else {
      safeStartApp();
    }
  }
}

function setThemeColor(color: string) {
  let theme = document.querySelector<HTMLMetaElement>("meta[name=theme-color]");
  if (!theme) {
    theme = document.createElement("meta");
    theme.name = "theme-color";
    document.head.appendChild(theme);
  }
  theme.setAttribute("content", color);
}

function setIcon() {
  let link = <HTMLLinkElement>document.querySelector("link[rel~='icon']");
  const logoSmall = sessionStorage.getItem("ORGANIZATION_LOGO_SMALL")
    ? sessionStorage.getItem("ORGANIZATION_LOGO_SMALL")
    : null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.getElementsByTagName("head")[0].appendChild(link);
  }
  if (logoSmall != null) {
    link.href = logoSmall;
  }

  let linkApple = <HTMLLinkElement>(
    document.querySelector("link[rel~='apple-touch-icon']")
  );
  const logoSmallApple = sessionStorage.getItem("ORGANIZATION_LOGO_SMALL")
    ? sessionStorage.getItem("ORGANIZATION_LOGO_SMALL")
    : null;
  if (!linkApple) {
    linkApple = document.createElement("link");
    linkApple.rel = "apple-touch-icon";
    document.getElementsByTagName("head")[0].appendChild(linkApple);
  }
  if (logoSmallApple != null) {
    linkApple.href = logoSmallApple;
  }
}

if (keyAnalytics === "" || keyAnalytics == null) {
  getAnalyticsKey();
} else if (keyAnalytics.includes("GTM")) {
  GoogleTagManagerService.loadGTM(keyAnalytics);
}

function setNRORGToAnalytics() {
  if (testNRORG) {
    return "1";
  } else {
    return sessionStorage.getItem("NRORG");
  }
}

function getAnalyticsKey() {
  try {
    const req = new Request();
    req.blockTouchEvents = false;
    req.body = {
      NRORG: setNRORGToAnalytics(),
    };
    req.route = "getAnalyticsKey";
    req.callbackSuccess = function (response: any) {
      keyAnalytics = response.response[0].googleAnalyticsKey;
      gtmAuth = response.response[0].gtmAuth;
      gtmPreview = response.response[0].gtmPreview;
      sessionStorage.setItem("ANALYTICS_KEY", keyAnalytics);
      if (keyAnalytics === "" || keyAnalytics == null) {
        Vue.use(VueAnalytics, {
          id: keyAnalytics,
          debug: {
            sendHitTask: false,
          },
        });
      } else if (keyAnalytics.includes("GTM")) {
        GoogleTagManagerService.loadGTM(keyAnalytics);
      } else {
        Vue.use(VueAnalytics, {
          id: keyAnalytics,
          debug: {
            sendHitTask: false,
          },
        });
      }
    };
    req.callbackError = function (error: any) {
      console.log(error);
    };
    req.showLoader = false;
    req.send();
  } catch (e) {
    console.log(e);
  }
}

function isWebView(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /wv|webview/i.test(userAgent);
}

function startApp() {
  if (!isWebView()) {
    initializeFirebase();
  }
  if (keyAnalytics === "" || keyAnalytics == null) {
    Vue.use(VueAnalytics, {
      id: keyAnalytics,
      debug: {
        sendHitTask: false,
      },
    });
  } else if (keyAnalytics.includes("GTM")) {
    GoogleTagManagerService.loadGTM(keyAnalytics);
  }

  new Vue({
    router,
    store,
    vuetify,
    render: (h) => h(App),
  }).$mount("#app");
}

// impede a exibição do banner de pwa no rodapé
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
});
